import {
  BASE_URL,
  handleDynamicRequest,
  useDispatch,
  useNavigate,
} from "../../Common/CommonExport";
import { useSelector } from "react-redux";
import {
  logo,
  testSheet,
  clockIcon,
  QuestionHelp,
  Cognizant,
  PracticeTest,
  CheckBox,
  CodeBox,
  HelpIcon,
  WhoWeAreImgpowerd,
} from "../../Common/CommonImages";

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  capitalize,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Zoom,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import {
  AccessTime,
  Business,
  CalendarMonth,
  CheckCircleOutline,
  Close,
  HelpOutline,
  QuizOutlined,
  ArrowForward,
  ArrowBack,
  InfoOutlined,
} from "@mui/icons-material";
import {
  setTestId,
  setTestName,
  setCandiateSettings,
  setTestTime,
  setTestDate,
  setTestInstructions,
  setTestSettings,
  setCompany,
  setTotalScore,
  setTotalQuestions,
  setLogout,
  setTestLink,
  setTestStatus,
  setClientId,
  setJobId,
  setHasPromoVideo,
} from "../../store/Test/action";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssessmentHeader from "../../Components/AssessmentHeader";

// ── helpers ──────────────────────────────────────────────────────────────────
const titleCase = (str) =>
  str
    ?.split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") ?? "";

const formatDate = (iso) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });



// ── Info card ─────────────────────────────────────────────────────────────────
function InfoCard({ icon, label, value, color }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: { base: "100%", sm: "30%" },
        bgcolor: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: "12px",
        p: { base: 2, sm: 2.5 },
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          bgcolor: `${color}20`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
      </Box>
      <Box>
        <Typography
          sx={{ fontSize: "11px", color: "#888", fontWeight: 500, mb: 0.3 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#333",
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function Summary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [testSummary, setTestSummary] = useState({});
  const [endDate, setEndDate] = useState("");
  const [test, setTest] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  const dispatch = useDispatch();
  const testSettings = useSelector((state) => state.testReducer.setting);
  const hasPromoVideo = useSelector((state) => state.testReducer.hasPromoVideo);

  const link = window.location.href;
  const parts = link.split("-");
  const uniqueId = parts[parts.length - 1];

  const hasSystemCheck = testSettings?.some(
    (s) => s.setting === "Restrict peripheral device usage" && s.active,
  );

  // ── API calls ──────────────────────────────────────────────────────────────
  const handleSummary = (id) => {
    handleDynamicRequest(
      "GET",
      `${BASE_URL}/public/assessment/${id}`,
      {},
      (error) => {
        if (error?.response?.status === 404) navigate("/notaccessible");
      },
      (data) => {
        if (!data) return;
        const d = data.data;
        setTestSummary(d);
        dispatch(setTestId(d._id));
        dispatch(setTestLink(d.link));
        dispatch(
          setTestSettings(
            d?.proctoringSettingsId?.map((s) => ({
              setting: s?.setting?.setting,
              active: s?.active,
            })),
          ),
        );
        dispatch(setCompany(d.createdBy?.clientId?.companyName));
        dispatch(
          setClientId(d.createdBy?.clientId?._id || d.createdBy?.clientId),
        );
        const clientId = d.createdBy?.clientId?._id || d.createdBy?.clientId;
        dispatch(setJobId(d.jobId || d.jobId?._id));
        dispatch(setTotalScore(d.totalScore));
        dispatch(setTestInstructions(d.instruction));
        dispatch(setCandiateSettings(d.candidateFieldsId));
        dispatch(setTestName(titleCase(d.name)));

        // Always fetch promo-video-config to ensure we have the latest state
        if (clientId) {
          handleDynamicRequest(
            "GET",
            `${BASE_URL}/public/promo-video-config/effective?clientId=${clientId}&module=assessment`,
            {},
            () => {
              dispatch(setHasPromoVideo(false));
              setLoading(false);
            },
            (promoData) => {
              if (promoData?.success && promoData?.data?.show) {
                dispatch(setHasPromoVideo(true));
              } else {
                dispatch(setHasPromoVideo(false));
              }
              setLoading(false);
            }
          );
        } else {
          dispatch(setHasPromoVideo(false));
          setLoading(false);
        }

        const startsAt = moment(d?.startsAt).format("DD MMM YYYY");
        const endsAt = d?.endsAt
          ? moment(d?.endsAt).format(" DD MMM YYYY")
          : " NA";
        dispatch(setTestDate({ startsAt, endsAt }));
        setEndDate(endsAt);

        dispatch(setTestStatus(d.status));
        const now = new Date();
        if (d.status !== "Published" || new Date(d.endsAt) < now) {
          navigate("/notaccessible");
        }
      },
      () => {
        setLoading(false);
        navigate("/notaccessible");
      }
    );
  };

  const handleTest = (id) => {
    handleDynamicRequest(
      "GET",
      `${BASE_URL}/public/assessment/${id}/summary`,
      {},
      () => {},
      (data) => {
        if (data) {
          setTest(data.data);
          dispatch(setTotalQuestions(data.data.totalQuestions));
        }
      },
    );
  };

  useEffect(() => {
    dispatch(setLogout());
    handleSummary(uniqueId);
    handleTest(uniqueId);
  }, []);

  useEffect(() => {
    if (testSummary?.testDuration) {
      const mins = parseInt(testSummary.testDuration);
      dispatch(
        setTestTime(
          moment
            .utc(moment.duration(mins, "minutes").asMilliseconds())
            .format("HH:mm:ss"),
        ),
      );
    }
  }, [testSummary?.testDuration, dispatch]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handlePage = () => {
    const now = new Date();
    const startsAtObj = new Date(testSummary.startsAt);
    const endsAtObj = new Date(testSummary.endsAt);

    if (startsAtObj < now) {
      if (!endDate || endsAtObj > now) {
        navigate("/enter-details");
      } else {
        toast.error("Test End Date is over");
      }
    } else {
      toast.error("Test Start Date is About to start");
    }
  };

  // ── Formatted duration ─────────────────────────────────────────────────────
  const formattedDuration = testSummary?.testDuration
    ? moment
        .utc(
          moment
            .duration(parseInt(testSummary.testDuration), "minutes")
            .asMilliseconds(),
        )
        .format("HH:mm:ss")
    : "";

  // ── Question type chips ────────────────────────────────────────────────────
  const questionTypes = Object.keys(test).filter(
    (k) => k !== "totalQuestions" && !k.startsWith("total") && test[k] > 0,
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
        <CircularProgress sx={{ color: "#FF6812" }} />
      </Box>
    );
  }

  return (
    <>
      {testSummary && (
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor: "#f5f7fa",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ToastContainer />

          {/* ── Unified Header: Logo + Steps ── */}
          <AssessmentHeader activeStep="Summary" hasSystemCheck={hasSystemCheck} />

          {/* ── Main Content ── */}
          <Box
            sx={{
              flex: 1,
              py: { base: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: { base: "95%", sm: "75%" },
                mx: "auto",
              }}
            >
              {/* ── Card ── */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 2px 20px rgba(0,0,0,0.07)",
                  overflow: "hidden",
                }}
              >
                {/* Card Header */}
                <Box
                  sx={{
                    padding: { base: "12px 16px", sm: "14px 24px" },
                    background:
                      "linear-gradient(135deg, #fff 60%, #fff8f4 100%)",
                  }}
                >
                  <Stack
                    direction={{ base: "column", sm: "row" }}
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    {/* Left: Icon + Test name + company */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #00897b, #26a69a)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 3px 10px rgba(0,137,123,0.3)",
                        }}
                      >
                        <img src={testSheet} alt="" style={{ width: 26 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: { base: "15px", sm: "18px" },
                            fontWeight: 700,
                            color: "#1a1a2e",
                            lineHeight: 1.3,
                            mb: 0.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: { base: "normal", sm: "nowrap" },
                          }}
                        >
                          {titleCase(testSummary?.name)}
                        </Typography>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.6}
                        >
                          <Tooltip title="Company" arrow>
                            <Business sx={{ fontSize: 15, color: "#aaa" }} />
                          </Tooltip>
                          <Typography sx={{ fontSize: "14px", color: "#777" }}>
                            {testSummary?.createdBy?.clientId?.companyName}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>

                    {/* Right: Timer + Help */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      flexShrink={0}
                    >
                      {formattedDuration && (
                        <Chip
                          icon={
                            <AccessTime
                              sx={{
                                fontSize: "16px !important",
                                color: "#FF6812 !important",
                              }}
                            />
                          }
                          label={formattedDuration}
                          sx={{
                            bgcolor: "#fff3ec",
                            border: "1px solid #ffd4b8",
                            color: "#FF6812",
                            fontWeight: 700,
                            fontSize: "13px",
                            height: 34,
                            "& .MuiChip-icon": { ml: 1 },
                          }}
                        />
                      )}
                      <Button
                        variant="outlined"
                        startIcon={<HelpOutline sx={{ fontSize: 15 }} />}
                        onClick={() => setHelpOpen(true)}
                        sx={{
                          height: 34,
                          borderColor: "#ddd",
                          color: "#555",
                          fontSize: "13px",
                          textTransform: "none",
                          borderRadius: "8px",
                          "&:hover": {
                            borderColor: "#FF6812",
                            color: "#FF6812",
                            bgcolor: "#fff8f4",
                          },
                        }}
                      >
                        Help
                      </Button>
                    </Stack>
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: "#bdbdbd" }} />

                {/* Info Cards Row */}
                <Box sx={{ padding: "16px 24px" }}>
                  <Stack
                    direction={{ base: "column", md: "row" }}
                    spacing={2}
                    flexWrap="wrap"
                  >
                    <InfoCard
                      icon={<CalendarMonth />}
                      label="Test Window"
                      color="#1976d2"
                      value={
                        testSummary?.startsAt && testSummary?.endsAt
                          ? `${formatDate(testSummary.startsAt)} – ${formatDate(testSummary.endsAt)}`
                          : "—"
                      }
                    />
                    <InfoCard
                      icon={<QuizOutlined />}
                      label="Total Questions"
                      color="#00897b"
                      value={
                        test?.totalQuestions
                          ? `${test.totalQuestions} Questions`
                          : "—"
                      }
                    />
                    <InfoCard
                      icon={<InfoOutlined />}
                      label="Question Types"
                      color="#7b1fa2"
                      value={
                        questionTypes.length
                          ? questionTypes
                              .map(
                                (k) =>
                                  `${test[k]} ${capitalize(k.split("Questions")[0])}`,
                              )
                              .join(" · ")
                          : "—"
                      }
                    />
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: "#bdbdbd" }} />

                {/* Test Description */}
                {testSummary?.description && (
                  <>
                    <Box sx={{ padding: "16px 24px" }}>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: "#1a1a2e",
                          mb: 1,
                        }}
                      >
                        Test Description
                      </Typography>
                      <Box
                        sx={{
                          fontSize: "14px",
                          color: "#555",
                          lineHeight: 1.7,
                          "& p": { m: 0 },
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            testSummary.description.charAt(0).toUpperCase() +
                            testSummary.description.slice(1),
                        }}
                      />
                    </Box>
                    <Divider sx={{ borderColor: "#bdbdbd" }} />
                  </>
                )}

                {/* General Instructions */}
                {testSummary?.instruction?.length > 0 && (
                  <Box sx={{ padding: "16px 24px" }}>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#1a1a2e",
                        mb: 1.5,
                      }}
                    >
                      General Instructions
                    </Typography>
                    <Stack spacing={1.2}>
                      {testSummary.instruction.map((inst, i) => (
                        <Stack
                          key={i}
                          direction="row"
                          alignItems="flex-start"
                          spacing={1.5}
                        >
                          <Box
                            sx={{
                              mt: "4px",
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: "#fff3ec",
                              border: "1.5px solid #FF6812",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "10px",
                                fontWeight: 700,
                                color: "#FF6812",
                              }}
                            >
                              {i + 1}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#444",
                              lineHeight: 1.6,
                            }}
                          >
                            {inst}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Practice Test Banner */}
                <Box
                  sx={{
                    mx: 2,
                    padding: "12px 16px",
                    mb: 3,
                    bgcolor: "#f0faf8",
                    border: "1px solid #b2dfdb",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <img src={PracticeTest} alt="" style={{ width: 24 }} />
                  <Typography
                    sx={{ fontSize: "13px", color: "#00695c", fontWeight: 500 }}
                  >
                    Want to warm up? Try{" "}
                    <ClickAwayListener onClickAway={() => {}}>
                      <Tooltip
                        title="Try HireCorrecto's Practice test."
                        placement="top"
                        TransitionComponent={Zoom}
                        arrow
                      >
                        <span
                          style={{
                            color: "#FF6812",
                            fontWeight: 700,
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          HireCorrecto's Practice Test
                        </span>
                      </Tooltip>
                    </ClickAwayListener>{" "}
                    before you begin.
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: "#bdbdbd" }} />
                {/* ── CTA Button ── */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { base: "column", sm: "row" },
                    justifyContent: hasPromoVideo !== false ? "space-between" : "flex-end",
                    alignItems: "center",
                    px: { base: "16px", sm: "24px" },
                    py: "16px",
                    gap: { base: 2, sm: 0 },
                  }}
                >
                  {hasPromoVideo !== false && (
                    <Button
                      onClick={() => {
                        const parts = window.location.pathname.split("/").filter(Boolean);
                        if (parts[0] === "summary") {
                          navigate(`/${parts[1]}/${parts[2]}`);
                        } else {
                          navigate(-1);
                        }
                      }}
                      startIcon={<ArrowBack />}
                      sx={{
                        height: 40,
                        borderColor: "#ddd",
                        color: "#555",
                        fontSize: "13px",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "10px",
                        border: "1.5px solid #e0e0e0",
                        px: 3,
                        width: { base: "100%", sm: "auto" },
                        "&:hover": {
                          borderColor: "#FF6812",
                          color: "#FF6812",
                          bgcolor: "#fff8f4",
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      Back to Video
                    </Button>
                  )}
                  <Button
                    onClick={handlePage}
                    endIcon={<ArrowForward />}
                    sx={{
                      background: "linear-gradient(135deg, #FF6812, #ff9a5c)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: { base: "14px", sm: "15px" },
                      borderRadius: "10px",
                      textTransform: "none",
                      boxShadow: "0 4px 16px rgba(255,104,18,0.35)",
                      width: { base: "100%", sm: "auto" },
                      "&:hover": {
                        background: "linear-gradient(135deg, #e55a0f, #ff8533)",
                        boxShadow: "0 6px 20px rgba(255,104,18,0.45)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    Proceed to Next Step
                  </Button>
                </Box>
              </Box>

              {/* ── Footer ── */}
              <Box
                sx={{ mt: 4, mb: 2, display: "flex", justifyContent: "center" }}
              >
                <img
                  src={WhoWeAreImgpowerd}
                  alt="Powered by HireCorrecto"
                  style={{ height: 28, width: 200 }}
                />
              </Box>
            </Box>
          </Box>

          {/* ── Help Dialog ── */}
          <Dialog
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            PaperProps={{
              sx: {
                borderRadius: "16px",
                maxWidth: 420,
                width: "90%",
                p: 1,
              },
            }}
          >
            <DialogContent>
              <Stack
                alignItems="center"
                spacing={2}
                sx={{ pt: 1, pb: 1, textAlign: "center" }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "#fff3ec",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img src={HelpIcon} alt="" style={{ width: 32 }} />
                </Box>
                <Typography
                  sx={{ fontSize: "18px", fontWeight: 700, color: "#1a1a2e" }}
                >
                  Help & Support
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#666" }}>
                  Please contact the test administrator at the following email
                  address.
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#f5f7fa",
                    borderRadius: "8px",
                    px: 3,
                    py: 1.5,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 600, color: "#FF6812" }}
                  >
                    {testSummary?.createdBy?.email}
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
              <Button
                onClick={() => setHelpOpen(false)}
                variant="contained"
                sx={{
                  bgcolor: "#FF6812",
                  color: "#fff",
                  borderRadius: "8px",
                  px: 4,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#e55a0f" },
                }}
              >
                Got it
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
}

export default Summary;
